"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlanEntitlementService = exports.defaultGetEntitlementSettings = void 0;
const models_1 = require("@buildwithflux/models");
const shared_1 = require("@buildwithflux/shared");
const config_1 = require("../config");
const context_1 = require("../functions/context");
const models_2 = require("../models");
exports.defaultGetEntitlementSettings = {
    includeInactivePlans: false,
};
/**
 * Service that determines entitlements for a given context
 *
 * This service takes a simple plan context, containing only the basic details about where the entitlements are being
 * used. It finds additional necessary details to discover all relevant plans. Then it resolves the plans to a set
 * of merged entitlements and returns them.
 */
class PlanEntitlementService {
    logger;
    constructor(logger = shared_1.silentLogger) {
        this.logger = logger;
    }
    /**
     * The main entrypoint for this service: given a detailed context, return a set of merged entitlements
     */
    getEntitlements(context, settings = exports.defaultGetEntitlementSettings) {
        const eligiblePlans = this.getEligiblePlans(context, settings);
        const results = Object.fromEntries(Object.values(config_1.EntitlementKey.enum).map((key) => {
            return [key, this.getEntitlementResult(key, context, eligiblePlans)];
        }));
        /*
         * We are declaring the type total here because we iterated over EntitlementKeys and always returned a result
         * for every key, so the result is a total mapping of all EntitlementKeys, and this cast should be safe.
         */
        return results;
    }
    /**
     * A secondary entrypoint useful for getting a single entitlement result at a time - mainly only used from the
     * backend, because the frontend will retrieve all entitlements at once
     */
    getEntitlement(key, context, settings = exports.defaultGetEntitlementSettings) {
        const eligiblePlans = this.getEligiblePlans(context, settings);
        return this.getEntitlementResult(key, context, eligiblePlans);
    }
    /**
     * A public method useful for unit testing, because it allows us to pass in the eligible plans directly, rather than
     * having to set up a context that directly matches the expected plans
     */
    getEntitlementFromEligiblePlans(key, context, eligiblePlans) {
        return this.getEntitlementResult(key, context, eligiblePlans);
    }
    /**
     * Gets the filtered eligible plans for the given context
     */
    getEligiblePlans(context, settings) {
        return {
            user: this.filterEligiblePlans(config_1.allUserPlans, context, settings),
            workspace: context.type === "organization" || context.type === "enterprise"
                ? this.filterEligiblePlans(config_1.allWorkspacePlans, context, settings)
                : [],
        };
    }
    /**
     * Gets a set of plan configs that match the current context, and optionally filters them for plans that are active
     *
     * These plans are eligible because they match the context, but they may not be active and able to contribute
     * entitlements, unless the second argument is passed as "false"
     */
    filterEligiblePlans(plansToFilter, context, { includeInactivePlans }) {
        const plans = [];
        plansToFilter
            .filter((plan) => {
            if (plan.contextType !== context.type) {
                return false;
            }
            if (!plan.matches(context)) {
                return false;
            }
            const status = plan.status(context);
            return ((includeInactivePlans ?? false) ||
                status === models_1.SubscriptionStatus.enum.active ||
                status === models_1.SubscriptionStatus.enum.trialing ||
                status === models_1.SubscriptionStatus.enum.incomplete);
        })
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            .forEach((plan) => plans.push(plan));
        if (globalThis?.__FLUX__?.logPlans) {
            this.logger.debug("Found plans for context", {
                plans: Array.from(plans),
                includeInactivePlans,
                ...(0, context_1.getLogInformationForContext)(context),
            });
        }
        return plans;
    }
    getEntitlementResult(key, context, eligiblePlans) {
        const entitlementType = config_1.EntitlementTypeForKey[key];
        const compareFn = models_2.EntitlementCompareFunctions[entitlementType];
        const valuesFromUserContext = this.getValuesWithAttributionForContext(key, context, eligiblePlans.user);
        if (context.type === "user") {
            return this.valuesToResult(key, eligiblePlans.user, valuesFromUserContext);
        }
        const valuesFromWorkspaceContext = this.getValuesWithAttributionForContext(key, context, eligiblePlans.workspace);
        const mergingBehavior = config_1.EntitlementWorkspaceBehaviorForKey[key];
        if (mergingBehavior === "override") {
            return this.valuesToResult(key, eligiblePlans.workspace, valuesFromWorkspaceContext);
        }
        // Otherwise, we are merging the user and workspace entitlements, and need to re-sort
        const combinedValues = [...valuesFromUserContext, ...valuesFromWorkspaceContext].sort((a, b) => compareFn(a, b));
        const combinedPlans = [...eligiblePlans.user, ...eligiblePlans.workspace];
        return this.valuesToResult(key, combinedPlans, combinedValues);
    }
    getValuesWithAttributionForContext(key, context, plans) {
        const entitlementType = config_1.EntitlementTypeForKey[key];
        const compareFn = models_2.EntitlementCompareFunctions[entitlementType];
        const valuesWithAttribution = Array.from(plans).flatMap((plan) => {
            const valueFromPlan = plan.entitlements[key];
            if (!valueFromPlan) {
                return [];
            }
            return [{ plan, status: plan.status(context), value: valueFromPlan }];
        });
        valuesWithAttribution.sort((a, b) => compareFn(a, b));
        return valuesWithAttribution;
    }
    valuesToResult(key, plans, valuesWithAttribution) {
        const entitlementType = config_1.EntitlementTypeForKey[key];
        const compareFn = models_2.EntitlementCompareFunctions[entitlementType];
        valuesWithAttribution.sort((a, b) => compareFn(a, b));
        if (valuesWithAttribution.length === 0) {
            return {
                key,
                value: models_2.DefaultEntitlementValues[config_1.EntitlementTypeForKey[key]],
                valueType: entitlementType,
                why: `${plans.length} plan${plans.length === 1 ? "" : "s"} matched the context, but no plan contributed any value for this entitlement - the default value was used`,
                contributingPlans: [],
            };
        }
        // After sorting, the actual result is the first value
        const result = valuesWithAttribution.pop();
        if (result === undefined) {
            // We can reliably expect this to never happen because we already checked the length a few lines up
            throw new Error("Unexpected logic error: no result in sorted array");
        }
        const { value, plan } = result;
        const contributingPlans = [plan];
        // Continue to look for further plans that contribute the same value
        while (valuesWithAttribution.length > 0) {
            const nextResult = valuesWithAttribution.pop();
            if (nextResult && nextResult.value === value) {
                contributingPlans.push(nextResult.plan);
            }
            else {
                break;
            }
        }
        return {
            key,
            value,
            valueType: entitlementType,
            why: `Value set by ${contributingPlans.length} plan${contributingPlans.length !== 1 ? "s" : ""}: ${contributingPlans.map((plan) => plan.key).join(", ")}`,
            contributingPlans,
        };
    }
}
exports.PlanEntitlementService = PlanEntitlementService;
//# sourceMappingURL=PlanEntitlementService.js.map